import React, { useState, useEffect } from "react"
import { useIntl, FormattedMessage } from "react-intl"
import Button from "react-bootstrap/Button"
import Form from "react-bootstrap/Form"
import InputGroup from "react-bootstrap/InputGroup"
import Container from "react-bootstrap/Container"
import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"

import { Search, PencilSquare, Trash, PlusCircle, Backspace, Building } from "react-bootstrap-icons" // Iconos de Bootstrap

import { AlertError, AlertLoading } from '../helpers/AlertHelper'

import FormModal from "../components/modals/FormModal"
import TableManagement from "../components/tables/TableManagement"

import ProvidersService from "../services/ProvidersService"

import ProvidersForm from "./ProvidersForm"

const Providers = () => {
    const intl = useIntl()
    const [providerId, setProviderId] = useState(null)
    const [providers, setProviders] = useState([])
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState(null)
    const [currPage, setCurrPage] = useState(1)
    const [search, setSearch] = useState("")
    const [sortBy, setSortBy] = useState("name")
    const [sortOrder, setSortOrder] = useState("asc")
    const [totalRows, setTotalRows] = useState(0)
    const [perPage, setPerPage] = useState(20)

    // Setting modal title
    const [modalTitle, setModalTitle] = useState(false)

    // Setting modal icon
    const [modalIcon, setModalIcon] = useState(false)

    // Open modal form control to add/edit element
    const [modalTrigger, setModalTrigger] = useState(false)

    // Setting modal action enum[add,edit,delete]
    const [modalAction, setModalAction] = useState(false)

    // Fetch data
    const fetchProviders = async () => {
        try {
            const response = await ProvidersService.getProviders(currPage, search, perPage, sortBy, sortOrder)
            setProviders(response.data.providers)
            setTotalRows(response.data.pagination.totalItems)
            setIsLoaded(true)
        } catch (err) {
            setIsLoaded(false)
            setError(err)
        }
    }

    const handleModal = () => {
        setModalTrigger(false)
        fetchProviders()
    }

    const handleSearchChange = (e) => {
        setSearch(e.target.value)
        setCurrPage(1) // Reinicia a la primera página cuando se busca
    }

    const handleSearchClean = () => {
        setSearch('')
        setCurrPage(1) // Reinicia a la primera página cuando se busca
    }

    // Re-fetch data on page/perPage change
    useEffect(() => {
        fetchProviders()
    }, [currPage, perPage, search, sortBy, sortOrder])

    const handleButtonClick = ({ data, type }) => {
        // Definir un objeto para mapear tipos a títulos e íconos
        const modalConfig = {
            add: {
                title: <FormattedMessage id="providersAdd" defaultMessage="Add provider" />,
                icon: <PlusCircle />,
            },
            edit: {
                title: <FormattedMessage id="providersEdit" defaultMessage="Edit provider" />,
                icon: <PencilSquare />,
            },
            delete: {
                title: <FormattedMessage id="providersDelete" defaultMessage="Delete provider" />,
                icon: <Trash />,
            }
        }

        // Establecer la configuración del modal dependiendo del tipo
        const config = modalConfig[type] || {}

        if (config) {
            setProviderId(type === 'add' ? false : data._id) // Si 'add', providerid será falso
            setModalTitle(config.title) // Set title from the config
            setModalIcon(config.icon) // Set icon from the config
            setModalAction(type)
            setModalTrigger(true)
        }
    }

    const handleRowsPerPageChange = (newPerPage, page) => {
        setPerPage(newPerPage)
        setCurrPage(page)
    }

    const handleSort = async (column, sortDirection) => {
        setSortOrder(sortDirection)
        setSortBy(column.id)
        setCurrPage(1)
    }

    const columns = [
        {
            id: 'name',
            name: <FormattedMessage id="providersName" defaultMessage="Name" />,
            selector: (row) => row.name,
            sortable: true,
        },
        {
            id: 'vat',
            name: <FormattedMessage id="providersVAT" defaultMessage="VAT number" />,
            selector: (row) => row.vat,
            sortable: true,
        },
        {
            id: 'email',
            name: <FormattedMessage id="providersEmail" defaultMessage="E-mail" />,
            selector: (row) => row.email,
            sortable: true,
        },
        {
            id: 'phone',
            name: <FormattedMessage id="providersPhoneNumber" defaultMessage="Phone" />,
            selector: (row) => row.phone,
        }
    ]


    return (
        <Container fluid className="p-0">
            <Row className="p-1 bg-secondary">
                <Col>
                    <InputGroup>
                        <InputGroup.Text><Search /></InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder={intl.formatMessage({ id: "textSearch", defaultMessage: "Search providers..." })}
                            value={search}
                            onChange={handleSearchChange}
                        />
                        <Button onClick={handleSearchClean} variant="light">
                            <Backspace /> <FormattedMessage id="textClean" defaultMessage="Clean" />
                        </Button>

                        <Button onClick={() => handleButtonClick({ data: null, type: 'add' })} variant="primary">
                            <PlusCircle /> <FormattedMessage id="textAdd" defaultMessage="Add" />
                        </Button>
                    </InputGroup>
                </Col>
            </Row>
            {isLoaded ? (
                <TableManagement
                    icon={<Building />}
                    title={<FormattedMessage id="navProviders" defaultMessage="Providers" />}
                    columns={columns}
                    data={providers}
                    paginationPerPage={perPage}
                    paginationTotalRows={totalRows}
                    onChangePage={setCurrPage}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    onSort={handleSort}
                    onActionClick={handleButtonClick}
                />
            ) : error ? (
                <AlertError error={error} />
            ) : (
                <AlertLoading isLoaded={isLoaded} />
            )}

            <FormModal
                title={modalTitle}
                icon={modalIcon}
                body={ProvidersForm}
                refreshData={handleModal}
                modalTrigger={modalTrigger}
                elementId={providerId}
                action={modalAction} />

        </Container>
    )
}

export default Providers